import { useEffect, useState, memo } from 'react';
import * as THREE from 'three';

interface VolumeDisplayProps {
  model: THREE.Group | null;
}

/**
 * Calculates the volume of a 3D model using the Signed Volume of Tetrahedrons.
 * Assumes 1 Three.js unit = 1 cm.
 */
function calculateVolume(object: THREE.Object3D): number {
  let totalVolume = 0;

  object.updateMatrixWorld(true);

  object.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      const geometry = child.geometry;
      const position = geometry.attributes.position;
      const index = geometry.index;
      
      const v1 = new THREE.Vector3();
      const v2 = new THREE.Vector3();
      const v3 = new THREE.Vector3();

      if (index) {
        for (let i = 0; i < index.count; i += 3) {
          v1.fromBufferAttribute(position, index.getX(i));
          v2.fromBufferAttribute(position, index.getX(i + 1));
          v3.fromBufferAttribute(position, index.getX(i + 2));

          // Apply world transformations
          v1.applyMatrix4(child.matrixWorld);
          v2.applyMatrix4(child.matrixWorld);
          v3.applyMatrix4(child.matrixWorld);

          totalVolume += v1.dot(v2.cross(v3)) / 6;
        }
      } else {
        for (let i = 0; i < position.count; i += 3) {
          v1.fromBufferAttribute(position, i);
          v2.fromBufferAttribute(position, i + 1);
          v3.fromBufferAttribute(position, i + 2);

          v1.applyMatrix4(child.matrixWorld);
          v2.applyMatrix4(child.matrixWorld);
          v3.applyMatrix4(child.matrixWorld);

          totalVolume += v1.dot(v2.cross(v3)) / 6;
        }
      }
    }
  });

  return Math.abs(totalVolume);
}

const VolumeDisplay = ({ model }: VolumeDisplayProps) => {
  const [volume, setVolume] = useState<number | null>(null);

  useEffect(() => {
    if (model) {
      // Calculate volume after a short delay to ensure transforms are ready
      const timer = setTimeout(() => {
        const vol = calculateVolume(model);
        setVolume(vol);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setVolume(null);
    }
  }, [model]);

  if (volume === null) return null;

  const volumeInLiters = volume / 1000;

  return (
    <div style={{ 
      marginTop: '15px', 
      padding: '12px', 
      background: 'rgba(0, 123, 255, 0.1)', 
      border: '1px solid rgba(0, 123, 255, 0.3)',
      borderRadius: '6px'
    }}>
      <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#0ea5e9' }}>📊 Model Volume</h3>
      <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'white' }}>
        {volume.toLocaleString(undefined, { maximumFractionDigits: 2 })} <span style={{ fontSize: '12px', fontWeight: 'normal', opacity: 0.8 }}>cm³</span>
      </div>
      <div style={{ fontSize: '13px', color: '#999', marginTop: '4px' }}>
        ≈ {volumeInLiters.toLocaleString(undefined, { maximumFractionDigits: 3 })} Liters
      </div>
      <div style={{ fontSize: '10px', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>
        * Assumes 1 unit = 1 cm. Correct for watertight models.
      </div>
    </div>
  );
};

export default memo(VolumeDisplay);
